import { Injectable } from '@angular/core';
import { Observable, from, BehaviorSubject, of, throwError } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import {
  PublicClientApplication,
  AccountInfo,
  AuthenticationResult,
  PopupRequest,
  RedirectRequest,
  SilentRequest,
  InteractionRequiredAuthError
} from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/msal.config';

/**
 * Interface para o serviço MSAL
 * Baseado no msal_service.dart do Flutter
 */
export interface IMsalService {
  // Inicialização
  initialize(): Observable<boolean>;

  // Autenticação
  loginPopup(request?: PopupRequest): Observable<AuthenticationResult>;
  loginRedirect(request?: RedirectRequest): Observable<void>;
  logout(): Observable<void>;

  // Tokens
  acquireTokenSilent(request: SilentRequest): Observable<AuthenticationResult>;
  acquireTokenPopup(request: PopupRequest): Observable<AuthenticationResult>;

  // Contas
  getAllAccounts(): AccountInfo[];
  getActiveAccount(): AccountInfo | null;
  setActiveAccount(account: AccountInfo | null): void;

  // Estado
  getAccessToken(scopes?: string[]): Observable<string>;
  isAuthenticated(): Observable<boolean>;
}

/**
 * Implementação do serviço MSAL
 * Baseado no msal_service.dart do Flutter
 */
@Injectable({
  providedIn: 'root'
})
export class MsalService implements IMsalService {
  private msalInstance: PublicClientApplication;
  private activeAccountSubject = new BehaviorSubject<AccountInfo | null>(null);
  private isInitialized = false;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
  }

  /**
   * Inicializa a instância MSAL
   */
  initialize(): Observable<boolean> {
    if (this.isInitialized) {
      return of(true);
    }

    return from(this.msalInstance.initialize()).pipe(
      switchMap(() => {
        // Aguardar um momento para garantir que a inicialização está completa
        return new Promise<boolean>(resolve => {
          setTimeout(() => {
            this.isInitialized = true;
            const accounts = this.msalInstance.getAllAccounts();
            if (accounts.length > 0) {
              this.setActiveAccount(accounts[0]);
            }
            resolve(true);
          }, 100); // Pequeno delay para garantir inicialização completa
        });
      }),
      catchError(error => {
        this.isInitialized = false;
        return of(false);
      })
    );
  }

  /**
   * Login com popup
   */
  loginPopup(request: PopupRequest = loginRequest): Observable<AuthenticationResult> {
    return from(this.msalInstance.loginPopup(request)).pipe(
      tap(response => {
        this.setActiveAccount(response.account);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Login com redirect
   */
  loginRedirect(request: RedirectRequest = loginRequest): Observable<void> {
    return from(this.msalInstance.loginRedirect(request)).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  /**
   * Logout
   */
  logout(): Observable<void> {
    const account = this.getActiveAccount();
    if (account) {
      return from(this.msalInstance.logoutPopup({
        account,
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri
      })).pipe(
        tap(() => {
          this.setActiveAccount(null);
        }),
        catchError(error => {
          return throwError(() => error);
        })
      );
    }
    return of(void 0);
  }

  /**
   * Adquire token silenciosamente
   */
  acquireTokenSilent(request: SilentRequest): Observable<AuthenticationResult> {
    return from(this.msalInstance.acquireTokenSilent(request)).pipe(
      catchError(error => {
        if (error instanceof InteractionRequiredAuthError) {
          return this.acquireTokenPopup(request);
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Adquire token com popup
   */
  acquireTokenPopup(request: PopupRequest): Observable<AuthenticationResult> {
    return from(this.msalInstance.acquireTokenPopup(request));
  }

  /**
   * Verifica se MSAL está inicializado
   */
  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('MSAL não foi inicializado. Chame initialize() primeiro.');
    }
  }

  /**
   * Obtém todas as contas
   */
  getAllAccounts(): AccountInfo[] {
    this.ensureInitialized();
    return this.msalInstance.getAllAccounts();
  }

  /**
   * Obtém a conta ativa
   */
  getActiveAccount(): AccountInfo | null {
    this.ensureInitialized();
    return this.msalInstance.getActiveAccount();
  }

  /**
   * Define a conta ativa
   */
  setActiveAccount(account: AccountInfo | null): void {
    this.ensureInitialized();
    this.msalInstance.setActiveAccount(account);
    this.activeAccountSubject.next(account);
  }

  /**
   * Obtém access token
   */
  getAccessToken(scopes: string[] = loginRequest.scopes): Observable<string> {
    const account = this.getActiveAccount();
    if (!account) {
      return throwError(() => new Error('Nenhuma conta ativa encontrada'));
    }

    const silentRequest: SilentRequest = {
      scopes,
      account
    };

    return this.acquireTokenSilent(silentRequest).pipe(
      map(response => response.accessToken)
    );
  }

  /**
   * Verifica se está autenticado
   */
  isAuthenticated(): Observable<boolean> {
    if (!this.isInitialized) {
      return of(false);
    }

    try {
      return of(this.getActiveAccount() !== null);
    } catch (error) {
      return of(false);
    }
  }
}
